import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, withDefaults, watch, useSlots } from 'vue';
import TrashSvg from '@/assets/icons/trash.svg';
import SvgPlus from "@/assets/icons/plus.svg";
import SvgMinus from "@/assets/icons/minus.svg";


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseInputNumber',
  props: {
    value: null,
    precision: null,
    min: null,
    max: null,
    maxLength: null,
    minLength: null,
    isValid: { type: Boolean },
    label: null,
    placeholder: null,
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    loading: { type: Boolean },
    clearable: { type: Boolean },
    clearableType: { default: 'inner' },
    view: { default: 'border-bottom' },
    size: { default: 'large' },
    inputTextAlign: null,
    sliceZeroNumbers: { type: Boolean },
    formatThousands: { type: Boolean },
    ifZeroSetNull: { type: Boolean },
    type: { default: 'default' }
  },
  emits: [
    'update:value',
    'input',
    'focus',
    'blur',
    'clear',
    'change',
],
  setup(__props: any, { emit: emits }) {

const props = __props as { value?: number | null, precision?: number, min?: number, max?: number, maxLength?: number, minLength?: number, isValid?: boolean, label?: string, placeholder?: string, disabled?: boolean, readonly?: boolean, loading?: boolean, clearable?: boolean, clearableType: 'inner' | 'out', view: 'border-bottom' | 'outlined', size: 'large' | 'medium' | 'small', inputTextAlign?: 'start' | 'center' | 'end', sliceZeroNumbers?: boolean, formatThousands?: boolean, ifZeroSetNull?: boolean, type: 'default' | 'stepper' }



const slots = useSlots();

const displayValue = ref('');
const isHandleLabelMove = ref<boolean>(false);
const inputRef = ref<HTMLElement | null>(null);

const getClasses = computed<string[]>(() => {
    return [
        (props.view === 'outlined' || props.type === 'stepper') && 'is-outlined',
        (props.view === 'border-bottom' && props.type !== 'stepper') && 'is-border-bottom',
        props.type === 'stepper' && 'is-stepper',
        props.loading && 'is-loading',
        props.size && `is-size-${props.size}`,
        props.isValid && 'is-valid',
    ];
});

const isShowClearable = computed<boolean>(() => {
    return props.clearable && (props.value || props.value === 0) && !props.readonly && !props.disabled;
});

const isLabelMove = computed<boolean>(() => {
    return props.label &&
        (isHandleLabelMove.value || props.value);
});

const countDigits = (value: number | string): number => {
    const str = String(value);
    return str.replace(/[^0-9]/g, '').length;
};

// Потолок целой части у бэкенда: Regex ^\d{1,13}(\.\d{1,4})?$ в revenue-service и payout-service.
const MAX_INTEGER_DIGITS = 13;
const DECIMAL_SEPARATOR = /[.,]/;

// Дробное поле без format-thousands - текстовое, со своим фильтром ввода: type="number"
// принимает точку и запятую по-разному в зависимости от локали браузера.
const isDecimalInput = computed<boolean>(() => {
    return !props.formatThousands && (props.precision ?? 0) > 0;
});

const inputType = computed<'text' | 'number'>(() => {
    return props.formatThousands || isDecimalInput.value ? 'text' : 'number';
});

// maxLength у дробного поля ограничивает только целую часть: до CRM-5542 разделитель
// не вводился, и maxLength фактически считал цифры целой части.
const maxIntegerDigits = computed<number>(() => {
    return Math.min(props.maxLength ?? MAX_INTEGER_DIGITS, MAX_INTEGER_DIGITS);
});

// Приводит ввод к виду «цифры.цифры». Разделителем считается последний из точек и запятых:
// во вставке «1 234,56» или «1,234.56» остальные - разряды.
const normalizeDecimal = (raw: string): string => {
    const cleaned = raw.replace(/[^\d.,]/g, '');
    const separatorIndex = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));

    if (separatorIndex === -1) {
        return cleaned;
    }

    const integerPart = cleaned.slice(0, separatorIndex).replace(/[.,]/g, '') || '0';
    const fractionalPart = cleaned.slice(separatorIndex + 1).slice(0, props.precision);

    return `${integerPart}.${fractionalPart}`;
};

const countIntegerDigits = (value: string): number => {
    return normalizeDecimal(value).split('.')[0].length;
};

const setInputValue = (target: HTMLInputElement, value: string, caret: number): void => {
    if (target.value === value) {
        return;
    }

    target.value = value;
    const position = Math.min(Math.max(caret, 0), value.length);
    target.setSelectionRange(position, position);
};

// Служебные клавиши и сочетания проходят, вставку дочищает applyDecimalInput.
const isDecimalKeyAllowed = (event: KeyboardEvent, target: HTMLInputElement): boolean => {
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey) {
        return true;
    }

    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const rest = target.value.slice(0, start) + target.value.slice(end);
    const separatorIndex = rest.search(DECIMAL_SEPARATOR);

    if (DECIMAL_SEPARATOR.test(event.key)) {
        return separatorIndex === -1;
    }

    if (!/^\d$/.test(event.key)) {
        return false;
    }

    return separatorIndex === -1 ||
        start <= separatorIndex ||
        rest.length - separatorIndex - 1 < (props.precision ?? 0);
};

// Правка, удлиняющая целую часть сверх лимита, откатывается целиком, а не обрезается:
// значение с сервера, уже длиннее лимита, не теряет цифры при правке дробной части.
const applyDecimalInput = (target: HTMLInputElement): void => {
    const raw = target.value;
    const caret = target.selectionStart ?? raw.length;
    const previous = displayValue.value ?? '';
    const next = normalizeDecimal(raw);
    const nextIntegerDigits = countIntegerDigits(next);

    if (nextIntegerDigits > maxIntegerDigits.value && nextIntegerDigits > countIntegerDigits(previous)) {
        setInputValue(target, previous, caret - (raw.length - previous.length));
        return;
    }

    setInputValue(target, next, normalizeDecimal(raw.slice(0, caret)).length);
    displayValue.value = next;
};

const isIncrementDisabled = computed<boolean>(() => {
    if (props.disabled || props.readonly) {
        return true;
    }

    const currentValue = props.value ?? 0;
    let testValue = currentValue + 1;

    if (props.precision !== undefined) {
        const step = Math.pow(10, -props.precision);
        testValue = currentValue + step;
        testValue = parseFloat(testValue.toFixed(props.precision));
    }

    if (props.max !== undefined && testValue > props.max) {
        return true;
    }

    if (props.maxLength !== undefined) {
        let testDisplay = String(testValue);
        if (props.precision !== undefined) {
            testDisplay = testValue.toFixed(props.precision);
        }
        
        const digitCount = countDigits(testDisplay);
        if (digitCount > props.maxLength) {
            return true;
        }
    }

    return false;
});

const isDecrementDisabled = computed<boolean>(() => {
    if (props.disabled || props.readonly) {
        return true;
    }

    const currentValue = props.value ?? 0;
    
    if (props.min !== undefined && currentValue <= props.min) {
        return true;
    }

    return false;
});

const handleKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLInputElement;

    if (props.formatThousands) {
        if (!/[\d\s,.\-+eE]/.test(event.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            return;
        }
    } else if (isDecimalInput.value) {
        if (!isDecimalKeyAllowed(event, target)) {
            event.preventDefault();
            return;
        }
    } else {
        if (['-', '+', 'e', 'E', '.'].includes(event.key)) {
            event.preventDefault();
            return;
        }
    }

    if (
        props.minLength &&
            props.minLength >= target.value.length &&
            ['Backspace', 'Delete', 'Shift'].includes(event.key)
    ) {
        event.preventDefault();
        return;
    }
};

const handleInput = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    let inputValue = target.value;

    if (isDecimalInput.value) {
        applyDecimalInput(target);
        inputValue = target.value;
    } else if (props.maxLength) {
        if (props.formatThousands) {
            const digitCount = inputValue.replace(/[^\d]/g, '').length;
            if (digitCount > props.maxLength) {
                const cleaned = inputValue.replace(/[^\d.,]/g, '');
                const parts = cleaned.split(/[,.]/);
                const integerPart = parts[0] || '';
                const fractionalPart = parts[1] || '';

                const maxIntegerDigits = props.precision !== undefined
                    ? props.maxLength - props.precision : props.maxLength;
                const integerDigitLimit = Math.max(maxIntegerDigits, 0);

                const finalInteger = integerPart.slice(0, integerDigitLimit);
                const finalFractional = props.precision !== undefined
                    ? fractionalPart.slice(0, props.precision) : fractionalPart;

                if (props.precision !== undefined && finalFractional) {
                    const integerForDisplay = finalInteger || '0';
                    inputValue = `${integerForDisplay}.${finalFractional}`;
                } else if (finalInteger) {
                    inputValue = finalInteger;
                } else {
                    inputValue = '0';
                }
                target.value = inputValue;
            }
        } else {
            if (props.maxLength <= inputValue.length) {
                target.value = inputValue.slice(0, props.maxLength);
            }
        }
    }

    if (inputValue && (props.min !== undefined || props.max !== undefined)) {
        const cleanedValue = inputValue.replace(/\s/g, '').replace(',', '.');
        const numValue = parseFloat(cleanedValue);
        
        if (!isNaN(numValue)) {
            let limitedValue: number | null = null;
                
            if (props.max !== undefined && numValue > props.max) {
                limitedValue = props.max;
            }
            
            if (props.min !== undefined && numValue < props.min) {
                limitedValue = props.min;
            }
            
            if (limitedValue !== null) {
                if (props.precision !== undefined) {
                    inputValue = limitedValue.toFixed(props.precision);
                } else {
                    inputValue = String(limitedValue);
                }
                target.value = inputValue;
                displayValue.value = inputValue;
            }
        }
    }

    emits('input', target.value);
};

const handleFocus = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    
    if (props.formatThousands && target.value) {
        const cleanedValue = target.value.replace(/\s/g, '').replace(',', '.');
        const numValue = parseFloat(cleanedValue);
        if (!isNaN(numValue)) {
            if (props.precision !== undefined) {
                target.value = numValue.toFixed(props.precision);
            } else {
                target.value = String(numValue);
            }
            displayValue.value = target.value;
        }
    }
    
    emits('focus', target.value);

    if (!(props.readonly || props.disabled)) {
        isHandleLabelMove.value = true;
    }
};

const handleBlur = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    
    if (target.value) {
        const { value, display } = parseValue(target.value);
        if (props.formatThousands) {
            displayValue.value = display;
        }
        emits('update:value', props.ifZeroSetNull && !value ? null : value);
    }
    
    emits('blur', target.value);

    if (!(props.readonly || props.disabled)) {
        isHandleLabelMove.value = false;
    }
};

const handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;

    const { value, display } = parseValue(!!target.value ? target.value : '0');
    displayValue.value = null;
    displayValue.value = display;

    emits('update:value', props.ifZeroSetNull && !value ? null : value);
    emits('change', target.value);
};

const handleClear = (): void => {
    emits('update:value', null);
    emits('input', null);
    emits('focus', null);
    emits('blur', null);
    emits('change', null);
    emits('clear', null);
};

const handleDecrement = (): void => {
    if (props.disabled || props.readonly) {
        return;
    }

    const currentValue = props.value ?? 0;
    let newValue = currentValue - 1;

    if (props.precision !== undefined) {
        const step = Math.pow(10, -props.precision);
        newValue = currentValue - step;
        newValue = parseFloat(newValue.toFixed(props.precision));
    }

    if (props.min !== undefined && newValue < props.min) {
        newValue = props.min;
    }

    const { value, display } = parseValue(String(newValue));
    displayValue.value = display;

    emits('update:value', props.ifZeroSetNull && !value ? null : value);
    emits('input', display);
    emits('change', display);
};

const handleIncrement = (): void => {
    if (props.disabled || props.readonly) {
        return;
    }

    const currentValue = props.value ?? 0;
    let newValue = currentValue + 1;

    if (props.precision !== undefined) {
        const step = Math.pow(10, -props.precision);
        newValue = currentValue + step;
        newValue = parseFloat(newValue.toFixed(props.precision));
    }

    if (props.max !== undefined && newValue > props.max) {
        newValue = props.max;
    }

    const { value, display } = parseValue(String(newValue));
    
    if (props.maxLength !== undefined) {
        const digitCount = countDigits(display);
        if (digitCount > props.maxLength) {
            let maxAllowedValue: number;
            
            if (props.precision !== undefined && props.precision > 0) {
                const maxIntegerDigits = props.maxLength - props.precision;
                const maxIntegerPart = Math.pow(10, maxIntegerDigits) - 1;
                const maxFractionalPart = (Math.pow(10, props.precision) - 1) / Math.pow(10, props.precision);
                maxAllowedValue = maxIntegerPart + maxFractionalPart;
            } else {
                maxAllowedValue = Math.pow(10, props.maxLength) - 1;
            }
            
            newValue = Math.min(newValue, maxAllowedValue);
            
            const { value: limitedValue, display: limitedDisplay } = parseValue(String(newValue));
            displayValue.value = limitedDisplay;
            emits('update:value', props.ifZeroSetNull && !limitedValue ? null : limitedValue);
            emits('input', limitedDisplay);
            emits('change', limitedDisplay);
            return;
        }
    }

    displayValue.value = display;

    emits('update:value', props.ifZeroSetNull && !value ? null : value);
    emits('input', display);
    emits('change', display);
};

// Без precision поле целое, граница показывается без дробной части.
const formatBound = (bound: number): string => {
    return props.precision !== undefined ? bound.toFixed(props.precision) : String(bound);
};

const parseValue = (event: string): { value: number, display: string } => {
    const cleanedEvent = event.replace(/\s/g, '').replace(',', '.');
    let value = parseFloat(cleanedEvent);
    let display = event;

    if (props.sliceZeroNumbers) {
        const numValue = parseFloat(cleanedEvent);
        if (!isNaN(numValue)) {
            const formatted = numValue.toLocaleString('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            display = formatted.replace(/,0+$/, '').replace(/,$/, '');
            value = numValue;
        } else {
            display = cleanedEvent.includes('.') ? Number(cleanedEvent).toFixed(2) : cleanedEvent;
        }
    } else if (props.precision !== undefined) {
        value = parseFloat(Number(cleanedEvent).toFixed(props.precision));
        const numValue = parseFloat(cleanedEvent);
        
        if (props.formatThousands && !isNaN(numValue)) {
            display = numValue.toLocaleString('ru-RU', {
                minimumFractionDigits: props.precision,
                maximumFractionDigits: props.precision,
            });
        } else if (!isNaN(numValue)) {
            display = Number(cleanedEvent).toFixed(props.precision);
        }
    } else if (props.formatThousands) {
        const numValue = parseFloat(cleanedEvent);
        if (!isNaN(numValue)) {
            display = numValue.toLocaleString('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
        }
    }

    if (props.min !== undefined && value < props.min) {
        const minDisplay = props.formatThousands
            ? props.min.toLocaleString('ru-RU', {
                minimumFractionDigits: props.precision ?? 0,
                maximumFractionDigits: props.precision ?? 2,
            }) : formatBound(props.min);
        display = minDisplay;
        value = parseFloat(props.min.toFixed(props.precision ?? 2));
    }

    if (props.max !== undefined && value > props.max) {
        const maxDisplay = props.formatThousands
            ? props.max.toLocaleString('ru-RU', {
                minimumFractionDigits: props.precision ?? 0,
                maximumFractionDigits: props.precision ?? 2,
            })
            : formatBound(props.max);
        display = maxDisplay;
        value = parseFloat(props.max.toFixed(props.precision ?? 2));
    }

    return { value, display };
};

watch(() => props.value, (value: number | null) => {
    if (value === null) {
        displayValue.value = '';
        return;
    }

    displayValue.value = parseValue(String(value)).display;
}, { immediate: true });

return { __sfc: true,props, emits, slots, displayValue, isHandleLabelMove, inputRef, getClasses, isShowClearable, isLabelMove, countDigits, MAX_INTEGER_DIGITS, DECIMAL_SEPARATOR, isDecimalInput, inputType, maxIntegerDigits, normalizeDecimal, countIntegerDigits, setInputValue, isDecimalKeyAllowed, applyDecimalInput, isIncrementDisabled, isDecrementDisabled, handleKeyDown, handleInput, handleFocus, handleBlur, handleChange, handleClear, handleDecrement, handleIncrement, formatBound, parseValue, TrashSvg, SvgPlus, SvgMinus }
}

})